// Saved preferences reach the screen through the shell's own functions, one per field.
import type { Preferences, PreferencesPatch } from "@cosmosai/shared";
import { applyDensity, currentDensity, DENSITY_KEY } from "../../app/shell/density";
import { applySidebarGroups, sidebarGroups } from "../../app/shell/sidebar-groups";
import { applyTheme, currentThemePreference, THEME_KEY } from "../../app/shell/theme";
import { applyLanguage, currentLanguage, LANGUAGE_KEY } from "../../i18n";

type Appliers = { [K in keyof Preferences]?: (value: Preferences[K]) => void };

// Favorites have no screen-wide effect: the screens that show stars read them.
const appliers: Appliers = {
  theme: applyTheme,
  language: (language) => void applyLanguage(language),
  density: applyDensity,
  sidebarGroups: applySidebarGroups,
};

/** Shows the given preferences without announcing them again. */
export function applyPreferences(patch: PreferencesPatch): void {
  for (const [field, value] of Object.entries(patch)) {
    const apply = appliers[field as keyof Preferences] as ((value: unknown) => void) | undefined;
    if (value !== undefined) apply?.(value);
  }
}

/** What this browser shows now, to save when the user has no saved preferences yet. */
export const localPreferences = (): PreferencesPatch => ({
  theme: currentThemePreference(),
  language: currentLanguage(),
  density: currentDensity(),
  sidebarGroups: sidebarGroups(),
});

/** Removes the first-paint caches, so the next visit starts from the defaults. */
export function forgetLocalPreferences(): void {
  for (const key of [THEME_KEY, LANGUAGE_KEY, DENSITY_KEY]) {
    try {
      localStorage.removeItem(key);
    } catch {
      // Storage blocked: nothing was cached.
    }
  }
}

/** The preferences with the patch's given fields replaced. */
export const withPatch = (base: Preferences, patch: PreferencesPatch): Preferences => ({
  ...base,
  ...(Object.fromEntries(
    Object.entries(patch).filter(([, value]) => value !== undefined),
  ) as Partial<Preferences>),
});
